import { createSignal, For, Match, Show, Switch } from "solid-js";
import { IconButton } from "../button/Button";
import { useTranslation } from "../../state/i18n";
import { Icon, type IconName } from "../icon/Icon";
import type { Status } from "../../types";
import ProgressBar from "../progressBar/ProgressBar";
import type { StatusButtonProps } from "../button/StatusButton";
import StatusButton from "../button/StatusButton";
import InlineAccordion from "../accordion/InlineAccordion";

type InlineBannerProps = {
  status: Status;
  title: string;
  description?: string;
  accordionExpandLabel?: string;
  accordionCollapseLabel?: string;
  progressBars?: {
    label: string;
    value: number;
    max: number;
  }[];
  primaryAction?: Omit<StatusButtonProps, "status" | "variant">;
  secondaryAction?: Omit<StatusButtonProps, "status" | "variant">;
  onDismiss?: () => void;
};

const classes = {
  upgrade: "text-status-upgrade bg-surface-status-subtle-upgrade border-status-subtle-upgrade",
  informative: "text-status-informative border-status-subtle-informative bg-surface-status-subtle-informative",
  warning: "text-status-warning border-status-subtle-warning bg-surface-status-subtle-warning",
  danger: "text-status-danger border-status-subtle-danger bg-surface-status-subtle-danger",
};

const iconClasses = {
  upgrade: "icon-status-upgrade",
  informative: "icon-status-informative",
  warning: "icon-status-warning",
  danger: "icon-status-danger",
};

const icon: Record<Status, IconName> = {
  upgrade: "sparkles",
  informative: "informationCircle",
  warning: "exclamation",
  danger: "exclamationCircle",
};

const InlineBanner = (props: InlineBannerProps) => {
  const { t } = useTranslation();
  const [accordionOpen, setAccordionOpen] = createSignal(true);

  return (
    <div class={`relative w-64 rounded-sm border p-3 ${classes[props.status]}`}>
      <div>
        <div class="flex flex-col gap-y-3">
          <div>
            <div class="flex gap-x-1.5">
              <Icon name={icon[props.status]} size="md" type="solid" class={`mt-px ${iconClasses[props.status]}`} />
              <div class="w-full pr-6.5 text-header-md">{props.title}</div>
            </div>
            <Show when={props.description}>
              <div class="mt-1 text-base-regular">{props.description}</div>
            </Show>
          </div>
          <Show when={props.progressBars}>
            {(progressBars) => (
              <Switch>
                <Match when={progressBars().length === 1}>
                  <div class="flex flex-col gap-y-3">
                    <For each={props.progressBars}>
                      {(bar) => (
                        <ProgressBar
                          variant="stepped"
                          backgroundColor="default"
                          label={bar.label}
                          status={props.status}
                          current={bar.value}
                          max={bar.max}
                        />
                      )}
                    </For>
                  </div>
                </Match>
                <Match when={progressBars().length > 1}>
                  <InlineAccordion
                    expandLabel={props.accordionExpandLabel ?? ""}
                    collapseLabel={props.accordionCollapseLabel ?? ""}
                    open={accordionOpen()}
                    setOpen={() => setAccordionOpen((prev) => !prev)}
                    variant={props.status}
                  >
                    <Show when={accordionOpen()}>
                      <div class="my-3 flex flex-col gap-y-3">
                        <For each={props.progressBars}>
                          {(bar) => (
                            <ProgressBar
                              variant="stepped"
                              backgroundColor="default"
                              label={bar.label}
                              status={props.status}
                              current={bar.value}
                              max={bar.max}
                            />
                          )}
                        </For>
                      </div>
                    </Show>
                  </InlineAccordion>
                </Match>
              </Switch>
            )}
          </Show>
        </div>
        <Show when={props.onDismiss}>
          <div class="absolute top-1 right-1">
            <IconButton
              aria-label={t(($) => $.BANNER_DISMISS_BUTTON_ARIA_LABEL)}
              variant="tertiary"
              size="medium"
              icon="x"
              onClick={props.onDismiss}
            />
          </div>
        </Show>
      </div>
      <Show when={props.primaryAction ?? props.secondaryAction}>
        <div class="mt-3 flex items-center gap-x-2">
          <Show when={props.secondaryAction}>
            {(secondaryActionProps) => (
              <StatusButton variant="secondary" status={props.status} {...secondaryActionProps()} />
            )}
          </Show>
          <Show when={props.primaryAction}>
            {(primaryActionProps) => <StatusButton variant="primary" status={props.status} {...primaryActionProps()} />}
          </Show>
        </div>
      </Show>
    </div>
  );
};

export default InlineBanner;
